import { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Search,
  FileSpreadsheet,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDebounce } from "use-debounce";

import Loading from "../components/common/LoaderCommon";
import { Button } from "../utils/components/ui/button";
import { Input } from "../utils/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../utils/components/ui/select";
import { formatWarrantyDate } from "./warranty/warrantyShared";
import {
  type WarrantyListItemApi,
  useGetAllWarranties,
  useDownloadWarrantyInvoicesExcel,
} from "../service/warrantyService";

type WarrantyFilter = "all" | "active" | "expired";
type WarrantyCoverageStatus = "Active" | "Expired";

type WarrantyListRow = {
  salesId: string;
  invoiceNumber: string;
  customerName: string;
  purchaseDate: string;
  warrantyStatus: WarrantyCoverageStatus;
  productCount: number;
};

const PAGE_SIZE = 10;

const normalizeWarrantyCoverageStatus = (
  value: string,
): WarrantyCoverageStatus =>
  value.toUpperCase() === "EXPIRED" ? "Expired" : "Active";

const normalizeWarrantyRow = (item: WarrantyListItemApi): WarrantyListRow => {
  const products = item.products ?? [];

  return {
    salesId: String(item.salesId),
    invoiceNumber: item.invoiceNumber,
    customerName: item.customerName,
    purchaseDate: item.purchaseDate,
    warrantyStatus: normalizeWarrantyCoverageStatus(
      products[0]?.warrantyStatus || "ACTIVE",
    ),
    productCount: products.length,
  };
};

function Warranty() {
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<WarrantyFilter>("all");
  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  const statusParam =
    statusFilter === "active"
      ? "ACTIVE"
      : statusFilter === "expired"
        ? "EXPIRED"
        : undefined;

  const {
    data: warrantyResponse,
    isLoading,
    isFetching,
  } = useGetAllWarranties(
    PAGE_SIZE,
    page,
    debouncedSearchValue.trim() || undefined,
    statusParam,
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchValue, statusFilter]);

  const { mutate: downloadExcel, isPending: isDownloading } =
    useDownloadWarrantyInvoicesExcel();

  const handleExport = () => {
    downloadExcel({
      invoiceNumber: debouncedSearchValue.trim() || undefined,
      status: statusParam,
    });
  };

  const rows = useMemo(
    () => (warrantyResponse?.content ?? []).map(normalizeWarrantyRow),
    [warrantyResponse],
  );

  if (isLoading) {
    return <Loading />;
  }

  const totalPages = Math.max(
    warrantyResponse?.totalPages ?? 0,
    rows.length > 0 ? 1 : 0,
  );
  const currentPage = (warrantyResponse?.pageNumber ?? page) + 1;
  const canGoPrevious = page > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages;

  return (
    <div className="flex min-h-full flex-col gap-6 pb-4">
      <div className="flex flex-1 flex-col overflow-hidden rounded-[28px] border border-[#DDE7FB] bg-white shadow-[0_18px_40px_-32px_rgba(15,23,42,0.45)]">
        <div className="flex flex-col gap-5 border-b border-[#E8EEF8] bg-[linear-gradient(180deg,#FFFFFF_0%,#F5F9FF_100%)] px-6 py-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                Warranty Register
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {warrantyResponse?.totalElements || rows.length} records found
                {isFetching ? " • Updating..." : ""}
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              disabled={isDownloading}
              onClick={handleExport}
              className="h-11 rounded-xl border-[#BFD1FF] px-5 text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE] flex items-center gap-2"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Export to Excel
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1.4fr)_240px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search warranty number"
                className="h-12 rounded-2xl border-[#E6ECF5] bg-[#F8FAFC] pl-11"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                setStatusFilter(value as WarrantyFilter)
              }
            >
              <SelectTrigger className="h-12 w-full rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 overflow-x-auto">
          <table className="min-w-[980px] w-full divide-y divide-[#EEF2F6]">
            <thead className="sticky top-0 z-10 bg-[#F8FAFC]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Invoice Number
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Customer Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Purchase Date
                </th>
                {/* <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Warranty Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Products
                </th> */}
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EEF2F6] bg-white">
              {rows.map((invoice) => (
                <tr
                  key={invoice.salesId}
                  className="transition hover:bg-[#FAFCFF]"
                >
                  <td className="px-6 py-5 font-semibold text-slate-900">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-6 py-5 text-sm text-slate-700">
                    {invoice.customerName}
                  </td>
                  <td className="px-6 py-5 text-sm text-slate-600">
                    {formatWarrantyDate(invoice.purchaseDate)}
                  </td>
                  {/* <td className="px-6 py-5">
                    <span
                      className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${
                        invoice.warrantyStatus === "Expired"
                          ? "border-[#FDE7C7] bg-[#FFF7ED] text-[#C2410C]"
                          : "border-[#DBEAFE] bg-[#EFF6FF] text-[#1D4ED8]"
                      }`}
                    >
                      {invoice.warrantyStatus}
                    </span>
                  </td> */}
                  {/* <td className="px-6 py-5 text-sm text-slate-600">
                    {invoice.productCount}
                  </td> */}
                  <td className="px-6 py-5 text-right">
                    <Button
                      asChild
                      type="button"
                      className="rounded-xl bg-[#1A5EF9] text-white hover:bg-[#0E4FDE]"
                    >
                      <Link to={`/warranty/${invoice.salesId}`}>
                        View Details
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-sm text-slate-500"
                  >
                    No warranty records match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-[#EEF2F6] px-6 py-4">
          <p className="text-sm text-slate-500">Show {rows.length} Entries</p>
          <div className="flex items-center gap-2">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E6ECF5] text-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canGoPrevious}
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1A5EF9] text-sm font-semibold text-white shadow-[0_16px_24px_-18px_#1A5EF9]">
              {currentPage}
            </button>
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#BFD1FF] text-[#1A5EF9] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canGoNext}
              onClick={() => setPage((prev) => prev + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Warranty;
